import React, { useMemo, useState } from 'react';
import { format } from 'date-fns-tz';
import { Button, Dropdown, Form, Icon, Modal, Loader } from 'semantic-ui-react';
import styled from 'styled-components';
import { HeaderCell, Table, TableBody, TableCell, TableHeader, TableRow, } from '../../../common/components/Table';
import { debriefScheduleApi } from '../api/debriefScheduleApi';
import { assessmentPeriodApi } from '../api/assessmentPeriodApi';
import {
  ASSESSMENT_PERIOD_IN_PROGRESS,
  SCHEDULE_CLOSED,
  SCHEDULE_COMPLETED,
  SCHEDULE_LIVE,
  SCHEDULE_NOT_STARTED,
} from '../constants';

import DoughnutGauge from 'areas/dashboard/components/DoughnutGauge';
import { Employee } from 'common/types/Employee';
import { DebriefSchedule } from 'store/generated/portal/assessments/debrief-schedules';
import StatusLabel from 'common/components/StatusLabel';
import { DeleteConfirmationModal } from "./DeleteConfirmationModal";
import { StyledDateTimeInput } from 'common/components/DateTimeInput';
import { FieldLabel, FormRow } from 'common/styles/page';
import { ReminderModal } from './AssessmentSurveyDetailsModals';
import { useToasts } from 'common/toast';

type AssessmentSurveyListProps = {
  selectedAssessmentPeriodId: number;
  assessmentPeriodStatus?: number;
  assessmentPeriodStartDate?: string;
  assessmentPeriodEndDate?: string;
  onViewDetails: (id: number) => void;
  onDelete: () => void;
  canManageAssessments: boolean;
  teamLeaders: Employee[];
  orgAdmins: Employee[];
};

const AssessmentSurveyList = ({
  selectedAssessmentPeriodId,
  assessmentPeriodStatus,
  assessmentPeriodStartDate,
  assessmentPeriodEndDate,
  onViewDetails,
  onDelete,
  canManageAssessments,
  teamLeaders,
  orgAdmins,
}: AssessmentSurveyListProps) => {

  const [deleteDebriefSchedule] =
    debriefScheduleApi.useApiPortalV1AssessmentsDebriefSchedulesDestroyMutation({});

  const [updateDebriefSchedule] =
    debriefScheduleApi.useApiPortalV1AssessmentsDebriefSchedulesPartialUpdateMutation({});

  const [updateAssessmentPeriod] =
    assessmentPeriodApi.useApiPortalV1AssessmentsPartialUpdateMutation({});

  const [sendRemindersToSchedule] =
    debriefScheduleApi.useApiPortalV1AssessmentsDebriefSchedulesSendRemindersCreateMutation();

  const { data, isLoading, isFetching } = debriefScheduleApi.useApiPortalV1AssessmentsDebriefSchedulesListQuery(
    { assessmentPeriodId: selectedAssessmentPeriodId },
  );

  const isLoadingSchedules = isLoading || isFetching;

  const projectMembers: Employee[] = useMemo(
    () =>
      [...teamLeaders, ...orgAdmins].sort((a, b) =>
        (a.firstName || '').localeCompare(b.firstName || '')
      ),
    [teamLeaders, orgAdmins]
  );

  const sortedData = data?.slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const SchedulesHeader: React.FC = () => (
    <TableHeader>
      <HeaderCell width={4}>Team Schedules</HeaderCell>
      <HeaderCell width={3}>Start</HeaderCell>
      <HeaderCell width={3}>End</HeaderCell>
      <HeaderCell width={2}>Status</HeaderCell>
      <HeaderCell width={1}></HeaderCell>
    </TableHeader>
  );

  // State for delete confirmation
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<DebriefSchedule | null>(null);

  // State for edit modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editData, setEditData] = useState<Partial<DebriefSchedule>>({});
  const [assessmentPeriodReminderData, setAssessmentPeriodReminderData] = useState<{
    reminder_enabled: boolean;
    reminder_schedule: string | null;
  }>({ reminder_enabled: false, reminder_schedule: null });
  const [assessmentPeriodId, setAssessmentPeriodId] = useState<number | null>(null);

  // Reminder confirmation modal state
  const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);
  const [reminderTarget, setReminderTarget] = useState<DebriefSchedule | null>(null);

  const { addToast } = useToasts();

  const handleDelete = (row: DebriefSchedule) => {
    if (!canManageAssessments) {
      return;
    }
    setSelectedRow(row);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (!canManageAssessments || !selectedRow) {
      return;
    }
    deleteDebriefSchedule({ id: selectedRow.id });
    setIsDeleteModalOpen(false);
  };

  function getScheduleStatusBadge(schedule: DebriefSchedule) {
    const now = Date.now();
    const closeAt = schedule.close_at ? new Date(schedule.close_at).getTime() : null;

    // Status → color
    const colorMap: Record<number, string> = {
      [SCHEDULE_COMPLETED]: '#2e7d32',
      [SCHEDULE_CLOSED]: '#2e7d32',
      [SCHEDULE_LIVE]: '#1976d2',
      [SCHEDULE_NOT_STARTED]: '#7A7A7A',
    };

    // Status → text label
    const labelMap: Record<number, string> = {
      [SCHEDULE_COMPLETED]: 'Completed',
      [SCHEDULE_CLOSED]: 'Closed',
      [SCHEDULE_LIVE]: 'In Progress',
      [SCHEDULE_NOT_STARTED]: 'Scheduled',
    };

    // Special case: schedule is live but end date passed
    if (schedule.status === SCHEDULE_LIVE && closeAt && closeAt < now) {
      return <StatusLabel color="#ef6c00" title="Awaiting Closure">Awaiting Closure</StatusLabel>;
    }

    return <StatusLabel color={colorMap[schedule.status] ?? 'grey'} title={labelMap[schedule.status] ?? 'Unknown'}>{labelMap[schedule.status] ?? 'Unknown'}</StatusLabel>;
  }


  const handleEdit = (row: DebriefSchedule) => {
    if (!canManageAssessments) {
      return;
    }
    setSelectedRow(row);
    setEditData({
      ...row,
      scheduled_for: row.scheduled_for ? new Date(row.scheduled_for).toISOString() : '',
      close_at: row.close_at ? new Date(row.close_at).toISOString() : '',
      leader: row.leader,
    });

    // Set AssessmentPeriod ID for reminder schedule editing
    const apId = (row as any).assessment_period;
    if (apId) {
      setAssessmentPeriodId(apId);
      // Reset reminder data - will be fetched via query hook
      setAssessmentPeriodReminderData({ reminder_enabled: false, reminder_schedule: null });
    } else {
      setAssessmentPeriodId(null);
    }

    setIsEditModalOpen(true);
  };

  // Fetch AssessmentPeriod data when editing
  const { data: assessmentPeriodData } = assessmentPeriodApi.useApiPortalV1AssessmentsRetrieveQuery(
    { id: assessmentPeriodId! },
    { skip: !assessmentPeriodId || !isEditModalOpen }
  );

  // Use passed-in status if available, otherwise fetch it
  const { data: currentAssessmentPeriod, isLoading: isLoadingAssessmentPeriod } = assessmentPeriodApi.useApiPortalV1AssessmentsRetrieveQuery(
    { id: selectedAssessmentPeriodId },
    { skip: !selectedAssessmentPeriodId || (assessmentPeriodStatus !== undefined && assessmentPeriodStartDate !== undefined && assessmentPeriodEndDate !== undefined) }
  );

  const effectiveStatus = assessmentPeriodStatus ?? currentAssessmentPeriod?.status;
  const isInProgress = effectiveStatus === ASSESSMENT_PERIOD_IN_PROGRESS;

  const handleSendRemindersToSchedule = async (scheduleId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canManageAssessments || !scheduleId) return;

    try {
      // The API expects both id and debriefSchedule in the body
      // We pass an empty object as debriefSchedule since the backend only needs the ID from the URL
      const result = await sendRemindersToSchedule({
        id: scheduleId,
        debriefSchedule: {} as any
      }).unwrap();
      if (result.success) {
        alert(`Successfully sent ${result.count} reminder${result.count !== 1 ? 's' : ''} for this team.`);
      } else {
        alert(`Failed to send reminders: ${result.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      alert(`Error sending reminders: ${error?.data?.error || error?.message || 'Unknown error'}`);
    }
  };

  // Update reminder data when AssessmentPeriod data loads
  React.useEffect(() => {
    if (assessmentPeriodData && isEditModalOpen) {
      const schedule = (assessmentPeriodData as any).reminder_schedule;
      // Handle both string (cron) and legacy dict formats
      const cronSchedule = typeof schedule === 'string' ? schedule : (schedule?.cron || null);
      setAssessmentPeriodReminderData({
        reminder_enabled: (assessmentPeriodData as any).reminder_enabled ?? false,
        reminder_schedule: cronSchedule,
      });
    }
  }, [assessmentPeriodData, isEditModalOpen]);

  const saveEdit = async () => {
    if (!canManageAssessments || !selectedRow) {
      return;
    }

    await updateDebriefSchedule({
      id: selectedRow.id,
      patchedDebriefScheduleWrite: {
        scheduled_for: String(editData?.scheduled_for),
        close_at: String(editData?.close_at),
        leader: editData?.leader,
      },
    });

    // Update AssessmentPeriod reminder schedule if assessment_period_id exists
    if (assessmentPeriodId) {
      await updateAssessmentPeriod({
        id: assessmentPeriodId,
        patchedAssessmentPeriod: {
          reminder_enabled: assessmentPeriodReminderData.reminder_enabled,
          reminder_schedule: assessmentPeriodReminderData.reminder_schedule,
        },
      });
    }

    setIsEditModalOpen(false);
  };

  // Confirm sending reminders for a single team (from modal)
  const confirmSendReminders = async () => {
    if (!canManageAssessments || !reminderTarget) return;

    try {
      const result = await sendRemindersToSchedule({ id: reminderTarget.id, debriefSchedule: {} as any }).unwrap();

      if (result.success) {
        addToast(`Successfully sent ${result.count} reminder${result.count !== 1 ? 's' : ''} for this team.`, { appearance: 'success' });
      } else {
        addToast(`Failed to send reminders: ${result.error || 'Unknown error'}`, { appearance: 'error' });
      }
    } catch (error: any) {
      addToast(`Error sending reminders: ${error?.data?.error || error?.message || 'Unknown error'}`, { appearance: 'error' });
    } finally {
      setIsReminderModalOpen(false);
      setReminderTarget(null);
    }
  };

  // If schedules are loading, show an inline loader in the table so the layout stays consistent
  if (isLoadingSchedules) {
    return (
      <Table>
        <SchedulesHeader />
        <TableBody>
          <TableRow>
            <TableCell colSpan={5}>
              <CenteredLoaderWrapper>
                <Loader active inline="centered" size="small">Loading schedules...</Loader>
              </CenteredLoaderWrapper>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    );
  }

  // If there are no schedules to show, render an empty-state row instead of nothing
  if (sortedData && sortedData.length === 0) {
    return (
      <Table>
        <SchedulesHeader />
        <TableBody>
          <TableRow>
            <TableCell colSpan={5}>No team schedules available.</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    );
  }


  function hasEmailFailures(schedule: DebriefSchedule): boolean {
    // Assuming the API returns participants on the schedule
    const participants = (schedule as any).participants as {
      lastEmailSuccessful?: boolean | null;
    }[] | undefined;

    if (!Array.isArray(participants) || participants.length === 0) {
      return false;
    }

    // "Failure" = most recent email was not successful
    return participants.some(p => p.lastEmailSuccessful === false);
  }

  return (
    <>
      <Table>
        <SchedulesHeader />
        <TableBody>
          {sortedData?.map(schedule => (
            <TableRow
              $selectable={true}
              key={schedule.id}
              onClick={() => {
                onViewDetails(schedule.id);
              }}
            >
              <TableCell $firstcol>
                <div style={{ display: 'flex', gap: 10 }}>
                  <DoughnutGauge
                    complete={
                      schedule.invitationCount > 0
                        ? (schedule.responseCount / schedule.invitationCount) * 100
                        : 0
                    }
                    percent={true}
                    size="tiny"
                  />

                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div>{schedule.name}</div>
                    <div style={{ fontSize: '11px', color: '#666', marginTop: 2 }}>
                      {schedule.invitationCount} participant{schedule.invitationCount !== 1 ? 's' : ''} • {schedule.responseCount} response{schedule.responseCount !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
              </TableCell>

              <TableCell>
                {schedule.scheduled_for
                  ? format(new Date(schedule.scheduled_for), 'MMM. d, yyyy (EEE)')
                  : 'N/A'}
                <br />
                {schedule.scheduled_for
                  ? format(new Date(schedule.scheduled_for), 'h:mm a z')
                  : 'N/A'}
              </TableCell>
              <TableCell>
                {schedule.close_at
                  ? format(new Date(schedule.close_at), 'MMM. d, yyyy (EEE)')
                  : 'N/A'}
                <br />
                {schedule.close_at
                  ? format(new Date(schedule.close_at), 'h:mm a z')
                  : 'N/A'}
              </TableCell>
              <TableCell>
                {(() => {
                  const hasFailures = hasEmailFailures(schedule);

                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>

                      {getScheduleStatusBadge(schedule)}
                      {schedule.status === SCHEDULE_LIVE && hasFailures && (
                        <Icon
                          name={'warning sign'}
                          color={'orange'}
                          size='large'
                          title="Emails were not delivered to some participants."
                        />
                      )}
                    </div>
                  );
                })()}
              </TableCell>

              <TableCell>
                <Dropdown
                  button
                  icon="ellipsis vertical"
                  floating
                  className="icon"
                  style={{ width: '38px', height: '35px', borderRadius: '8px' }}
                  direction="left"
                >
                  <Dropdown.Menu>
                    <Dropdown.Item
                      icon="history"
                      text="View Details"
                      onClick={e => {
                        e.stopPropagation();
                        onViewDetails(schedule.id);
                      }}
                    />
                    {canManageAssessments && (
                      <>
                        <Dropdown.Item
                          icon="send"
                          text="Remind Team Participants"
                          disabled={
                            (assessmentPeriodStatus === undefined && isLoadingAssessmentPeriod) || !isInProgress
                          }
                          onClick={e => {
                            e.stopPropagation();
                            setReminderTarget(schedule);
                            setIsReminderModalOpen(true);
                          }}
                        />
                        <Dropdown.Item
                          icon="write"
                          text="Edit"
                          disabled={
                            schedule.status !== SCHEDULE_NOT_STARTED &&
                            schedule.status !== SCHEDULE_LIVE
                          }
                          onClick={e => {
                            e.stopPropagation();
                            handleEdit(schedule);
                          }}
                        />
                        <Dropdown.Item
                          icon="trash"
                          text="Delete"
                          disabled={
                            schedule.status !== SCHEDULE_NOT_STARTED &&
                            schedule.status !== SCHEDULE_LIVE
                          }
                          onClick={e => {
                            e.stopPropagation();
                            handleDelete(schedule);
                          }}
                        />
                      </>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Edit Modal */}
      <Modal open={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} size="small">
        <Modal.Header>Edit Survey Schedule</Modal.Header>
        <Modal.Content>
          <Form>
            <FormRow>
              <FieldLabel>Start Date & Time</FieldLabel>
              <StyledDateTimeInput
                value={editData?.scheduled_for ?? null}
                onChange={val => setEditData({
                  ...editData,
                  scheduled_for: val ? new Date(val).toISOString() : undefined,
                })}
                hasValue={!!editData?.scheduled_for}
              />
            </FormRow>
            <FormRow>
              <FieldLabel>End Date & Time</FieldLabel>
              <StyledDateTimeInput
                value={editData?.close_at ?? null}
                onChange={val => setEditData({
                  ...editData,
                  close_at: val ? new Date(val).toISOString() : undefined,
                })}
                hasValue={!!editData?.close_at}
              />
            </FormRow>
            <FormRow>
              <FieldLabel>Leader</FieldLabel>
              <select
                value={editData?.leader ?? ''}
                onChange={e =>
                  setEditData({
                    ...editData,
                    leader: e.target.value === '' ? undefined : parseInt(e.target.value, 10),
                  })
                }
              >
                <option value="">* No Leader Assigned *</option>
                {projectMembers.map(member => (
                  <option key={member.id} value={member.id}>
                    {(member.firstName || member.lastName) ? `${member.lastName}${member.firstName ? `, ${member.firstName}` : ''}` : ''}
                  </option>
                ))}
              </select>
            </FormRow>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
          <Button color="blue" onClick={saveEdit}>
            Save
          </Button>
        </Modal.Actions>
      </Modal>

      <ReminderModal
        open={isReminderModalOpen}
        name={reminderTarget?.name ?? 'this team'}
        email={''}
        onClose={() => {
          setIsReminderModalOpen(false);
          setReminderTarget(null);
        }}
        onConfirm={confirmSendReminders}
      />

      <DeleteConfirmationModal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onClick={confirmDelete}
      />
    </>
  );
};

const CenteredLoaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 0;
`;

export default AssessmentSurveyList;
